import { motion } from 'framer-motion';
import { Image as ImageIcon } from 'lucide-react';
import { fadeInUp, staggerContainer } from '@/lib/animations';

const INDUSTRIES = [
  /*
  {
    image: '/img/trades.webp',
    label: 'Trades & Home Services',
    desc: 'Answers calls on site, captures job details and urgency.',
  },
  {
    image: '/img/sme.webp',
    label: 'Appointment-heavy SMEs',
    desc: 'Reduce repetitive call load & capture bookings.',
  },
  {
    image: '/img/smsf.webp',
    label: 'After-hour Enquiries',
    desc: 'Acknowledge customers and capture leads for next-day follow-up.',
  },*/
  {
    image: '/img/plumbing.webp',
    label: 'Plumbing',
    desc: 'Turn more urgent calls into booked jobs — even after hours.',
    useCase: 'Answers when you can\'t, identifies urgent jobs, captures the customer\'s issue and location, and sends the details straight to you.',
  },
  {
    image: '/img/electrician.webp',
    label: 'Electrical',
    desc: 'Spend less time answering calls and more time on paying jobs.',
    useCase: 'Handles incoming calls, filters out spam and sales calls, and captures the details of genuine electrical jobs for you to follow up.',
  },
  {
    image: '/img/hvac.webp',
    label: 'Air-conditioning/HVAC',
    desc: 'Stay on top of peak-season calls without adding office staff.',
    useCase: 'Handles multiple enquiries during busy periods, captures the problem and customer details, and keeps new jobs moving while you\'re on site.'
  ,
  },
  {
    image: '/img/solar.webp',
    label: 'Solar',
    desc: 'Capture and qualify new leads while they\'re still ready to buy.',
    useCase:'Collects key details like property type, energy needs and project timeframe, giving you what you need to prioritise and follow up with each lead.',
  },
  {
    image: '/img/landscaping.webp',
    label: 'Landscaping',
    desc: 'Keep new enquiries coming in while you\'re busy on the tools.',
    useCase: 'Captures the customer\'s job type, location, scope and preferred timing, so every enquiry is ready for you to quote or follow up.',
  },
  {
    image: '/img/construction.webp',
    label: 'Carpentry/Building',
    desc: 'Capture every opportunity without stopping work to answer the phone.',
    useCase:'Collects project details, location, scope and timeframe from new enquiries, so you can quickly decide which jobs are worth following up.',
  },
];

export default function IndustriesGrid() {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
      variants={staggerContainer}
      className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-6xl mx-auto"
    >
      {INDUSTRIES.map((industry) => {
        return (
          <motion.div
            key={industry.label}
            variants={fadeInUp}
            className="relative rounded-3xl overflow-hidden aspect-[3/4] group"
          >
            {industry.image ? (
              <img
                src={industry.image}
                alt={industry.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500"
              />
            ) : (
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: 'linear-gradient(145deg, rgba(var(--c-purple-rgb),0.35) 0%, rgba(var(--c-purple-rgb),0.12) 100%)' }}
              >
                <ImageIcon size={48} className="text-brand-blue-light" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6">
              <h3 className="text-lg font-bold text-white tracking-tight mb-1">{industry.label}</h3>
              <p className="text-sm text-white/80 leading-snug">{industry.desc}</p>
              {industry.useCase && (
                <p className="text-xs text-white/60 leading-relaxed mt-2">
                  <span className="font-semibold text-brand-blue-light">Use Case: </span>
                  {industry.useCase}
                </p>
              )}
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
